/**
 * Normalize a Typesense job document to the flattened Algolia hit shape used by SearchContext / SRP.
 */
import { applyTyomarkkinatoriSearchHitParity } from '../search/apply-tyomarkkinatori-search-hit-parity';
import { normalizeAlgoliaFacetScalarFieldsOnHit } from '../search/normalize-algolia-facet-scalar-fields';
import { resolveAlgoliaSearchHitObjectId } from '../search/resolve-algolia-search-hit-object-id';
import { algoliaGeolocFromTypesenseGeoloc } from './normalize-typesense-geoloc';

/** Typesense-only keys removed from Algolia-shaped hits. */
const TYPESENSE_ONLY_KEYS = new Set(['geoloc', 'companies', 'locationList', 'locale']);

/**
 * Algolia indexes `location` as string[]; Typesense stores a facet string (and optional `locationList`).
 */
export function algoliaLocationFromTypesenseDocument(
  document: Record<string, unknown>,
): string[] | undefined {
  const locationList = document.locationList;
  if (Array.isArray(locationList)) {
    const parts = locationList
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim());
    if (parts.length > 0) return parts;
  }

  const location = document.location;
  if (Array.isArray(location)) {
    const parts = location
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim());
    if (parts.length > 0) return parts;
  }

  if (typeof location === 'string') {
    const trimmed = location.trim();
    if (trimmed) return [trimmed];
  }

  return undefined;
}

function applyCompanyJoinFields(
  algoliaHit: Record<string, unknown>,
  document: Record<string, unknown>,
): void {
  if (document.companies && typeof document.companies === 'object') {
    const company = document.companies as Record<string, unknown>;
    if (!algoliaHit.companyName && company.name) {
      algoliaHit.companyName = company.name;
    }
    if (!algoliaHit.logoUrl && company.logo_url) {
      algoliaHit.logoUrl = company.logo_url;
    }
    if (!algoliaHit.companyURL && company.website) {
      algoliaHit.companyURL = company.website;
    }
    if (!algoliaHit.companySlug && company.slug) {
      algoliaHit.companySlug = company.slug;
    }
  }
}

function stripTypesenseOnlyFields(algoliaHit: Record<string, unknown>): void {
  for (const key of TYPESENSE_ONLY_KEYS) {
    delete algoliaHit[key];
  }
}

/**
 * Map one Typesense job document to an Algolia-compatible search hit, or null when objectID is missing.
 */
export function mapTypesenseDocumentToAlgoliaHit(
  document: Record<string, unknown>,
): Record<string, unknown> | null {
  const objectID = resolveAlgoliaSearchHitObjectId(document);
  if (!objectID) {
    return null;
  }

  const algoliaHit: Record<string, unknown> = { ...document, objectID, id: objectID };

  const location = algoliaLocationFromTypesenseDocument(document);
  if (location) {
    algoliaHit.location = location;
  }

  applyCompanyJoinFields(algoliaHit, document);

  if (
    (!algoliaHit.companyId || String(algoliaHit.companyId).trim() === '') &&
    typeof algoliaHit.companyName === 'string' &&
    algoliaHit.companyName.trim()
  ) {
    algoliaHit.companyId = algoliaHit.companyName.trim();
  }

  const geoloc = algoliaGeolocFromTypesenseGeoloc(document.geoloc);
  if (geoloc) {
    algoliaHit._geoloc = geoloc;
  }

  stripTypesenseOnlyFields(algoliaHit);
  normalizeAlgoliaFacetScalarFieldsOnHit(algoliaHit);
  applyTyomarkkinatoriSearchHitParity(algoliaHit);

  return algoliaHit;
}
